import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { PublicationKind } from "../DTOs/responses/research";
import { usePublications } from "../hooks/useResearch";
import { uploadedFileUrl } from "../utils/uploadedFileUrl";
import CustomCircularProgress from "./CustomCircularProgress";

type PublicationFilter = "all" | PublicationKind;

const titleFromCitation = (citation: string) => {
  const latexTitle = citation.match(/``(.+?)''/);
  if (latexTitle?.[1]) return latexTitle[1];

  const quotedTitle = citation.match(/"(.+?)"/);
  if (quotedTitle?.[1]) return quotedTitle[1];

  return citation;
};

const yearFromCitation = (citation: string) => {
  const years = citation.match(/\b(?:19|20)\d{2}\b/g);
  return years?.[years.length - 1] ?? "—";
};

// The paper is uploaded through the admin panel as a PDF; entries without one
// stay plain text.
const pdfUrl = (pdf: string | null) =>
  pdf ? uploadedFileUrl("pelgroupPublications", pdf) : "";

const publicationLabel = (kind: PublicationKind) =>
  kind === "journal" ? "Journal publication" : "Conference publication";

export default function Publications() {
  const [filter, setFilter] = useState<PublicationFilter>("all");
  const [query, setQuery] = useState("");
  const { data, isLoading, isError } = usePublications();

  const publications = useMemo(() => data ?? [], [data]);

  const journalCount = publications.filter((item) => item.kind === "journal").length;
  const conferenceCount = publications.filter((item) => item.kind === "conference").length;

  const filteredPublications = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return publications.filter((publication) => {
      const matchesFilter = filter === "all" || publication.kind === filter;
      const title = titleFromCitation(publication.citation);
      const matchesQuery =
        !normalizedQuery ||
        publication.id.toLowerCase().includes(normalizedQuery) ||
        title.toLowerCase().includes(normalizedQuery) ||
        publication.citation.toLowerCase().includes(normalizedQuery);

      return matchesFilter && matchesQuery;
    });
  }, [publications, filter, query]);

  if (isLoading) return <CustomCircularProgress color="#C46A2D" />;

  if (isError)
    return (
      <Typography sx={{ color: "#62605A", textAlign: "center" }}>
        Publications are unavailable right now.
      </Typography>
    );

  return (
    <Box sx={{ mt: { xs: 0, md: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          border: "1px solid #E4DED2",
          borderRadius: "10px",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(250,248,243,0.96) 58%, rgba(231,240,238,0.86) 100%)",
          boxShadow: "0 16px 38px rgba(32,36,42,0.07)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "flex-end" }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: "#C46A2D",
                fontFamily: "Source Sans Pro SemiBold",
                fontSize: 13,
                letterSpacing: "1.8px",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Publications
            </Typography>
            <Typography
              component="h2"
              sx={{
                color: "#20242A",
                fontFamily: "Source Sans Pro SemiBold",
                fontSize: { xs: 28, md: 36 },
                lineHeight: 1.12,
                mb: 1.5,
              }}
            >
              Research output
            </Typography>
            <Typography sx={{ color: "#62605A", fontSize: { xs: 15, md: 17 }, lineHeight: 1.7, maxWidth: 780 }}>
              Browse the group’s journal and conference publications. Use the filters or search by title,
              author, venue, year, or publication code.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Chip label={`${publications.length} total`} sx={{ bgcolor: "#20242A", color: "#FFFFFF", fontWeight: 700 }} />
            <Chip label={`${journalCount} journals`} sx={{ bgcolor: "#E7F0EE", color: "#1F4F4B", fontWeight: 700 }} />
            <Chip label={`${conferenceCount} conferences`} sx={{ bgcolor: "#F3E4D7", color: "#8A4319", fontWeight: 700 }} />
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          border: "1px solid #E4DED2",
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          position: "sticky",
          top: { xs: 72, md: 92 },
          zIndex: 2,
          boxShadow: "0 10px 28px rgba(32,36,42,0.06)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Tabs
            value={filter}
            onChange={(_, value: PublicationFilter) => setFilter(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              "& .MuiTabs-indicator": { backgroundColor: "#C46A2D", height: 3 },
              "& .MuiTab-root": {
                minHeight: 42,
                textTransform: "none",
                fontFamily: "Source Sans Pro SemiBold",
                color: "#62605A",
              },
              "& .Mui-selected": { color: "#20242A !important" },
            }}
          >
            <Tab value="all" label="All" />
            <Tab value="journal" label="Journals" />
            <Tab value="conference" label="Conferences" />
          </Tabs>

          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search publications"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start">⌕</InputAdornment>,
            }}
            sx={{
              maxWidth: { md: 420 },
              ml: { md: "auto !important" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#FAF8F3",
                "& fieldset": { borderColor: "#E4DED2" },
                "&:hover fieldset": { borderColor: "#D7B38D" },
                "&.Mui-focused fieldset": { borderColor: "#C46A2D" },
              },
            }}
          />
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {filteredPublications.map((publication) => {
          const title = titleFromCitation(publication.citation);
          const year = yearFromCitation(publication.citation);
          const isJournal = publication.kind === "journal";
          const href = pdfUrl(publication.pdf);

          return (
            <Paper
              key={publication.id}
              elevation={0}
              {...(href
                ? {
                    component: "a" as const,
                    href,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {})}
              sx={{
                p: { xs: 2.2, md: 2.8 },
                border: "1px solid #E4DED2",
                borderRadius: "10px",
                backgroundColor: "#FFFFFF",
                display: "block",
                textDecoration: "none",
                cursor: href ? "pointer" : "default",
                transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
                "&:hover": {
                  borderColor: isJournal ? "#9BC7C3" : "#D7B38D",
                  boxShadow: "0 16px 34px rgba(32,36,42,0.10)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2.2} alignItems={{ sm: "flex-start" }}>
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    flex: "0 0 58px",
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    color: isJournal ? "#1F4F4B" : "#8A4319",
                    backgroundColor: isJournal ? "#E7F0EE" : "#F3E4D7",
                    fontFamily: "Source Sans Pro SemiBold",
                    fontSize: 17,
                  }}
                >
                  {publication.id}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      label={publicationLabel(publication.kind)}
                      sx={{
                        height: 24,
                        bgcolor: isJournal ? "rgba(46,125,120,0.10)" : "rgba(196,106,45,0.10)",
                        color: isJournal ? "#1F4F4B" : "#8A4319",
                        fontWeight: 700,
                      }}
                    />
                    <Chip
                      size="small"
                      label={year}
                      sx={{ height: 24, bgcolor: "#FAF8F3", color: "#62605A", fontWeight: 700 }}
                    />
                    {href ? (
                      <Chip
                        size="small"
                        label="PDF"
                        sx={{ height: 24, bgcolor: "#20242A", color: "#FFFFFF", fontWeight: 700 }}
                      />
                    ) : null}
                  </Stack>

                  <Typography
                    component="h3"
                    sx={{
                      color: "#20242A",
                      fontFamily: "Source Sans Pro SemiBold",
                      fontSize: { xs: 18, md: 21 },
                      lineHeight: 1.25,
                      mb: 1,
                    }}
                  >
                    {title}
                  </Typography>

                  <Divider sx={{ borderColor: "#EFE8DC", mb: 1.3 }} />

                  <Typography sx={{ color: "#62605A", fontSize: { xs: 14, md: 15 }, lineHeight: 1.65 }}>
                    {publication.citation}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {filteredPublications.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid #E4DED2",
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Typography sx={{ color: "#62605A" }}>No publications match this search.</Typography>
        </Paper>
      )}
    </Box>
  );
}
